import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Paper, Chip, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const H2BVisaPage: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            {/* Header */}
            <Box sx={{ 
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                py: 8,
                marginTop: 8,
                textAlign: 'center'
            }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                        {t('h2bVisa.title', 'H2-B Visa')}
                    </Typography>
                    <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
                        {t('h2bVisa.subtitle', 'Temporary Non-Agricultural Workers Program - Connecting skilled workers with American businesses')}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Grid container spacing={4}>
                    {/* What is H2-B Visa */}
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                                    {t('h2bVisa.whatIsTitle', 'What is the H2-B Visa?')}
                                </Typography>
                                
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                                            {t('h2bVisa.whatIsDescription1', 'The H2-B visa is a non-immigrant visa that allows U.S. employers to bring foreign nationals to the United States to fill temporary non-agricultural jobs.')}
                                        </Typography>
                                        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                                            {t('h2bVisa.whatIsDescription2', 'This program addresses labor shortages in various sectors including hospitality, construction, landscaping, food service, and other seasonal or peak-load industries.')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                                            {t('h2bVisa.whatIsDescription3', 'The H2-B visa allows workers to stay in the U.S. for up to one year, with the possibility of extensions for up to three years in total.')}
                                        </Typography>
                                        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                                            {t('h2bVisa.requirementsDescription', 'Must be at least 18 years old, have relevant work experience, and be from a country that has a treaty with the U.S. for this type of visa.')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Key Benefits */}
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                                    {t('h2bVisa.benefitsTitle', 'Key Benefits of the H2-B Visa Program')}
                                </Typography>
                                
                                <Grid container spacing={3} justifyContent="center">
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2bVisa.benefit1', 'Legal Work')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2bVisa.benefit1Desc', 'Work legally in the U.S. with proper documentation')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2bVisa.benefit2', 'Fair Wages')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2bVisa.benefit2Desc', 'Guaranteed fair wages and working conditions')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2bVisa.benefit4', 'Transportation')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2bVisa.benefit4Desc', 'Transportation to and from the U.S. included')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* How Laboria Helps */}
                    <Grid item xs={12}>
                        <Paper 
                            sx={{ 
                                p: 4, 
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white',
                                textAlign: 'center',
                                mb: 4 
                            }}
                        >
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                {t('h2bVisa.howWeHelpTitle', 'How Laboria Helps with H2-B Visa Processing')}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                                {t('h2bVisa.howWeHelpSubtitle', 'We handle the entire process from start to finish')}
                            </Typography>
                            
                            <Grid container spacing={3} justifyContent="center">
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2bVisa.step1', '1. Application')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2bVisa.step1Desc', 'Complete visa application and documentation')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2bVisa.step2', '2. Processing')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2bVisa.step2Desc', 'Handle all government processing and approvals')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2bVisa.step3', '3. Support')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2bVisa.step3Desc', 'Ongoing support throughout the process')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2bVisa.step4', '4. Success')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2bVisa.step4Desc', 'Ensure successful visa approval and arrival')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Client Section */}
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                                    {t('h2bVisa.clientSectionTitle', 'For U.S. Businesses & Employers')}
                                </Typography>
                                
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                            {t('h2bVisa.clientBenefitsTitle', 'Why Choose H2-B Workers?')}
                                        </Typography>
                                        <Box component="ul" sx={{ pl: 2 }}>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientBenefit1', 'Reliable seasonal workforce during peak business periods')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientBenefit2', 'Skilled workers with relevant industry experience')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientBenefit3', 'Compliance with U.S. labor laws and regulations')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientBenefit4', 'Flexible employment periods (up to 3 years)')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                            {t('h2bVisa.clientRequirementsTitle', 'Employer Requirements')}
                                        </Typography>
                                        <Box component="ul" sx={{ pl: 2 }}>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientRequirement1', 'Demonstrate temporary or seasonal need for workers')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientRequirement3', 'Cover transportation costs to and from the U.S.')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.clientRequirement4', 'Ensure workers\' compensation coverage')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                
                                <Box sx={{ textAlign: 'center', mt: 4 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large"
                                        onClick={() => {
                                            navigate('/contact');
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        sx={{ 
                                            backgroundColor: theme.palette.secondary.main,
                                            fontSize: '1.1rem',
                                            padding: '12px 32px',
                                            '&:hover': {
                                                backgroundColor: theme.palette.secondary.dark
                                            }
                                        }}
                                    >
                                        {t('h2bVisa.clientCtaButton', 'Hire H2-B Workers')}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Applicant Section */}
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                                    {t('h2bVisa.applicantSectionTitle', 'For Workers Seeking U.S. Employment')}
                                </Typography>
                                
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                            {t('h2bVisa.applicantBenefitsTitle', 'Benefits for Workers')}
                                        </Typography>
                                        <Box component="ul" sx={{ pl: 2 }}>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantBenefit1', 'Legal work authorization in the United States')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantBenefit2', 'Competitive wages with fair working conditions')}
                                            </Typography>

                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantBenefit4', 'Round-trip transportation included')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                            {t('h2bVisa.applicantRequirementsTitle', 'Worker Requirements')}
                                        </Typography>
                                        <Box component="ul" sx={{ pl: 2 }}>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantRequirement1', 'Minimum 18 years of age')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantRequirement2', 'Proven work experience in relevant industry')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantRequirement3', 'Citizen of eligible treaty country')}
                                            </Typography>
                                            <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                                {t('h2bVisa.applicantRequirement4', 'Willingness to work temporary positions')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                
                                <Box sx={{ textAlign: 'center', mt: 4 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large"
                                        onClick={() => {
                                            navigate('/openings/request/1');
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        sx={{ 
                                            backgroundColor: theme.palette.primary.main,
                                            fontSize: '1.1rem',
                                            padding: '12px 32px',
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark
                                            }
                                        }}
                                    >
                                        {t('h2bVisa.applicantCtaButton', 'Apply for H2-B Jobs')}
                                    </Button>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        {t('h2bVisa.applicantCtaSubtext', 'Browse available positions and start your application')}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default H2BVisaPage;
